import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { CloudMark, Wordmark } from './components/CloudFrugalLogo';
import React, { useState, useCallback, useEffect } from 'react';
import { useTheme } from './hooks/useTheme';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ConnectAccount from './pages/ConnectAccount';
import AdminPage from './pages/AdminPage';
import Settings from './pages/Settings';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import AWSCheatSheet from './pages/AWSCheatSheet';

function AppHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  useTheme(); // needed to initialise theme on public pages

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { name: 'Features', href: '/#features' },
    { name: 'How It Works', href: '/#how-it-works' },
    { name: 'Frugal Cloud Guide', href: '/cheatsheet' },
    { name: 'Blog', href: '/blog' },
  ];

  const isAuthenticated = !!localStorage.getItem('token');
  const userInitial = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return '';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return (payload.email?.[0] || payload.name?.[0] || '?').toUpperCase();
    } catch { return '?'; }
  })();

  const { pathname } = useLocation();
  const navLinkStyle: React.CSSProperties = { color: '#374151', transition: 'color 0.2s', fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap' as const };

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        display: 'flex',
        justifyContent: 'center',
        padding: scrolled ? '0' : '6px 20px',
        pointerEvents: 'none',
        transition: 'padding 0.5s cubic-bezier(0.4,0,0.2,1)',
      }}>
      <nav style={{
        pointerEvents: 'all',
        width: '100%',
        maxWidth: scrolled ? '100%' : '980px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: scrolled ? '62px' : '52px',
        padding: scrolled ? '0 36px' : '0 24px',
        borderRadius: scrolled ? '0' : '14px',
        background: scrolled ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid ${scrolled ? 'rgba(15,61,46,0.08)' : 'transparent'}`,
        boxShadow: scrolled ? '0 1px 0 rgba(15,61,46,0.06), 0 8px 32px rgba(7,40,29,0.06)' : 'none',
        transition: 'max-width 0.5s cubic-bezier(0.4,0,0.2,1), height 0.5s cubic-bezier(0.4,0,0.2,1), border-radius 0.5s cubic-bezier(0.4,0,0.2,1), background 0.5s cubic-bezier(0.4,0,0.2,1), border-color 0.5s cubic-bezier(0.4,0,0.2,1), box-shadow 0.5s cubic-bezier(0.4,0,0.2,1), padding 0.5s cubic-bezier(0.4,0,0.2,1)',
      }}>
          {/* Logo — always left */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0">
            <CloudMark size={48} />
            <Wordmark size={18} />
          </Link>

          {/* Nav links — center, scrolls with animation */}
          <div className="hidden md:flex items-center gap-6 flex-1 justify-center">
            {navLinks.map((link) => {
              const linkPath = link.href.split('#')[0];
              const isActive = !link.href.includes('#') && linkPath !== '/' && pathname.startsWith(linkPath);
              return (
                <a key={link.name} href={link.href}
                  style={{
                    ...navLinkStyle,
                    color: isActive ? '#0F3D2E' : '#374151',
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? 'rgba(15,61,46,0.08)' : 'transparent',
                    padding: '4px 12px',
                    borderRadius: '99px',
                    boxShadow: isActive ? 'inset 0 -2px 0 #E8B84C' : 'none',
                  }}
                  onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLAnchorElement).style.color = '#0F3D2E'; }}
                  onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLAnchorElement).style.color = '#374151'; }}
                >
                  {link.name}
                </a>
              );
            })}
          </div>

          {/* CTA + avatar — always far right */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            {isAuthenticated ? (
              <>
                <button onClick={() => navigate('/dashboard')} className="px-5 py-2 rounded-lg text-sm font-semibold transition-all hover:brightness-110 flex items-center gap-1.5 whitespace-nowrap" style={{ background: '#E8B84C', color: '#07281D' }}>
                  Go to Dashboard <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                  style={{ background: '#0F3D2E' }}
                  title="Signed in"
                >
                  {userInitial}
                </div>
              </>
            ) : (
              <button onClick={() => navigate('/login')} className="px-5 py-2 rounded-lg text-sm font-semibold transition-all hover:brightness-110 flex items-center gap-1.5 whitespace-nowrap" style={{ background: '#E8B84C', color: '#07281D' }}>
                Get Started <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-gray-100">
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
      </nav>
      </div>

      {isMenuOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 z-40 bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className="block py-2 text-gray-700 hover:text-emerald-800" onClick={closeMenu}>
                {link.name}
              </a>
            ))}
            {isAuthenticated ? (
              <>
                <button onClick={() => { navigate('/dashboard'); closeMenu(); }} className="block w-full px-6 py-3 rounded-lg text-center font-semibold" style={{ background: '#E8B84C', color: '#07281D' }}>
                  Go to Dashboard
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { navigate('/login'); closeMenu(); }} className="block w-full text-left py-2 text-gray-700 hover:text-emerald-800">
                  Sign In
                </button>
                <button onClick={() => { navigate('/register'); closeMenu(); }} className="block w-full px-6 py-3 rounded-lg text-center font-semibold" style={{ background: '#E8B84C', color: '#07281D' }}>
                  Get Started
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const APP_ROUTES = ['/dashboard', '/connect-account', '/admin', '/settings'];

function AppShell() {
  const location = useLocation();
  const isAppRoute = APP_ROUTES.some(r => location.pathname.startsWith(r));

  return (
    <>
      {!isAppRoute && <AppHeader />}
      <div className={!isAppRoute ? 'pt-16' : ''}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/connect-account" element={<ConnectAccount />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/cheatsheet" element={<AWSCheatSheet />} />
        </Routes>
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
